import {mapEnglishToCapAllWord, mapScreamingSnakeCaseToEnglish, mapStringToEnumValue} from '@coolcolduk/util';
import {Button, MenuItem, Stack, TextField} from '@mui/material';
import {useEffect, useState} from 'react';
import validator from 'validator';
import {ContactCategory, UserAccount} from '../../__generated__/resolvers-types';

export interface ContactUsDialogFormProps {
  user: UserAccount | null | undefined;
  categories: string[];
  onSubmit: (
    name: string,
    email: string,
    category: ContactCategory,
    message: string,
    attachments: File[],
  ) => Promise<unknown>;
  onCancel?: () => void;
}

export default function ContactUsDialogForm({user, categories, onSubmit, onCancel}: ContactUsDialogFormProps) {
  const [category, setCategory] = useState<ContactCategory | undefined>();
  const [message, setMessage] = useState('');
  const [name, setName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [emailError, setEmailError] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    setName(user?.displayName || '');
    setEmail(user?.email || '');
  }, [user]);

  function clearForm() {
    setCategory(undefined);
    setMessage('');
    setName(user?.displayName || '');
    setEmail(user?.email || '');
    setEmailError('');
    setFiles([]);
  }

  const handleCancel = () => {
    clearForm();
    if (onCancel) onCancel();
  };

  const validateEmail = (emailToValidate: string): boolean => {
    if (!emailToValidate) {
      setEmailError('Email is required');
      return false;
    }
    if (!validator.isEmail(emailToValidate)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail) {
      validateEmail(newEmail);
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      return;
    }
    try {
      await onSubmit(name, email, category || ContactCategory.GENERAL, message, files);
      handleCancel();
    } catch (error) {
      console.error(error);
    }
  };

  const isReadyToSend = !!category && !!message && !!name && !!email && !emailError;

  return (
    <>
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        margin="normal"
        disabled={!!user}
      />
      <TextField
        label="Email"
        value={email}
        onChange={handleEmailChange}
        fullWidth
        margin="normal"
        disabled={!!user}
        error={!!emailError}
        helperText={emailError}
        type="email"
      />
      <TextField
        label="Category"
        select
        value={category || ''}
        onChange={(e) => setCategory(mapStringToEnumValue(ContactCategory, e.target.value))}
        fullWidth
        margin="normal"
      >
        {categories.map((cat) => (
          <MenuItem key={cat} value={cat}>
            {mapEnglishToCapAllWord(mapScreamingSnakeCaseToEnglish(cat))}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        fullWidth
        margin="normal"
        multiline
        rows={4}
      />
      {/* <Dropzone
        files={files}
        setFiles={setFiles}
        withPreview
        acceptedFileTypes={[
          MimeType.TEXT_CSV,
          MimeType.IMAGE_PNG,
          MimeType.IMAGE_JPEG,
          MimeType.IMAGE_GIF,
          MimeType.TEXT_PLAIN,
        ]}
      /> */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{mt: 2}}>
        {onCancel && (
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
        )}
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!isReadyToSend}>
          Send
        </Button>
      </Stack>
    </>
  );
}
